import { doc, getDoc } from 'firebase/firestore';
import { isValidPaymentDeposit } from '../booking/deposit';
import { db } from '../firebase/firebase';
import {
  BusinessConfig, Employee, MAX_SERVICE_QUESTIONS, Service, SERVICE_QUESTION_LABEL_LIMIT, ServiceQuestion,
  createEmptyBusinessConfig,
} from '../types';
import { validateTenantId } from '../tenancy/paths';
import { isPreviewBooking, previewEmployee, withPreviewEmployee } from '../booking/previewBooking';

type PublicSiteConfig = {
  tenantId: string;
  theme: {
    primaryColor: string;
    secondaryColor: string;
    backgroundColor: string;
    textColor: string;
  };
  business: {
    displayName: string;
    timezone: string;
    address?: string;
    phone?: string;
    email?: string;
    website?: string;
    businessHours: BusinessConfig['businessInfo']['businessHours'];
  };
  homepage: {
    heroTitle: string;
    heroSubtitle: string;
    about?: string;
    mapUrl?: string;
    navbarLogoVisible?: boolean;
    spotlightLogoVisible?: boolean;
    logoPlacement?: BusinessConfig['homepage']['logoPlacement'];
    gallery: Array<{ image: string; title?: string; description?: string }>;
    reviews: Array<{ name: string; text: string; rating: number }>;
  };
  assets: { logo?: string; hero?: string; heroLogo?: string };
  socials: BusinessConfig['socials'];
  sections: {
    showTeam: boolean;
    showReviews: boolean;
    showGallery: boolean;
    showAbout: boolean;
  };
  booking: {
    enabled: boolean;
    showPrices: boolean;
    showEmployeePicker: boolean;
    ctaLabel: string;
  };
};

type PublicStaffProjection = {
  id: string;
  firstName: string;
  lastName: string;
  displayName: string;
  bio?: string;
  image?: string;
  isOrchestrated: boolean;
  isOwner: boolean;
  canAcceptCard: boolean;
  socials?: Employee['socials'];
};

type PublicBookingConfigProjection = {
  features: BusinessConfig['features'];
  preferences: NonNullable<BusinessConfig['preferences']>;
};

type PublicOperationalProjections = {
  services: Array<{
    id: string;
    title: string;
    description?: string;
    price: number;
    durationMinutes: number;
    employeeIds: string[];
    image?: string;
    showOnHomepage?: boolean;
    priceVaries?: boolean;
    questions?: unknown;
  }>;
  staff: PublicStaffProjection[];
  bookingConfig: PublicBookingConfigProjection;
};

export type HomepageRuntimeConfig = BusinessConfig & {
  publicSiteRuntime: {
    source: 'legacy' | 'published';
    bookingEnabled: boolean;
    bookingCtaLabel: string;
    showPrices: boolean;
    showEmployeePicker: boolean;
    theme?: PublicSiteConfig['theme'];
  };
};

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isOptionalString = (value: unknown): boolean => value === undefined || typeof value === 'string';

const hasPublicStringValues = (value: Record<string, unknown>): boolean =>
  Object.values(value).every((entry) => isOptionalString(entry));

export const resolvePublishedLogoPlacement = (
  homepage: Pick<PublicSiteConfig['homepage'], 'logoPlacement' | 'navbarLogoVisible' | 'spotlightLogoVisible'>,
): BusinessConfig['homepage']['logoPlacement'] => {
  if (homepage.logoPlacement) return homepage.logoPlacement;
  if (homepage.navbarLogoVisible && homepage.spotlightLogoVisible) return 'both';
  if (homepage.spotlightLogoVisible) return 'heroOverlay';
  return 'navbar';
};

export const parsePublicSiteDocument = (
  tenantId: string,
  value: unknown
): { content: PublicSiteConfig; projections: PublicOperationalProjections } | null => {
  if (
    !isObject(value) ||
    value.tenantId !== tenantId ||
    ![1, 2].includes(Number(value.schemaVersion)) ||
    // A preview ("coming soon") site is readable only by the preview booking flow.
    !(value.availabilityState === 'active' || (value.availabilityState === 'coming_soon' && isPreviewBooking())) ||
    !isObject(value.content)
  ) {
    return null;
  }
  const content = value.content;
  if (
    content.tenantId !== tenantId ||
    !isObject(content.theme) ||
    !isObject(content.business) ||
    !isObject(content.homepage) ||
    !isObject(content.assets) ||
    !isObject(content.socials) ||
    !isObject(content.sections) ||
    !isObject(content.booking)
  ) return null;
  const business = content.business;
  const theme = content.theme;
  const homepage = content.homepage;
  const assets = content.assets;
  const socials = content.socials;
  const sections = content.sections;
  const booking = content.booking;
  const validLogoConfiguration =
    (homepage.logoPlacement === undefined ||
      ['navbar', 'heroOverlay', 'both'].includes(String(homepage.logoPlacement))) &&
    (homepage.navbarLogoVisible === undefined || typeof homepage.navbarLogoVisible === 'boolean') &&
    (homepage.spotlightLogoVisible === undefined || typeof homepage.spotlightLogoVisible === 'boolean');
  const validGallery = Array.isArray(homepage.gallery) && homepage.gallery.every((item) =>
    isObject(item) && typeof item.image === 'string' &&
    isOptionalString(item.title) && isOptionalString(item.description));
  const validReviews = Array.isArray(homepage.reviews) && homepage.reviews.every((review) =>
    isObject(review) && typeof review.name === 'string' && typeof review.text === 'string' &&
    typeof review.rating === 'number');
  if (
    !['primaryColor', 'secondaryColor', 'backgroundColor', 'textColor'].every(
      (key) => typeof theme[key] === 'string'
    ) ||
    typeof business.displayName !== 'string' ||
    typeof business.timezone !== 'string' ||
    !isObject(business.businessHours) ||
    !Object.values(business.businessHours).every((entry) => typeof entry === 'string') ||
    typeof homepage.heroTitle !== 'string' ||
    typeof homepage.heroSubtitle !== 'string' ||
    !validLogoConfiguration ||
    !validGallery ||
    !validReviews ||
    !hasPublicStringValues(assets) ||
    !hasPublicStringValues(socials) ||
    typeof sections.showTeam !== 'boolean' ||
    typeof sections.showReviews !== 'boolean' ||
    typeof sections.showGallery !== 'boolean' ||
    typeof sections.showAbout !== 'boolean' ||
    typeof booking.enabled !== 'boolean' ||
    typeof booking.showPrices !== 'boolean' ||
    typeof booking.showEmployeePicker !== 'boolean' ||
    typeof booking.ctaLabel !== 'string'
  ) return null;
  const projections = isObject(value.projections) ? value.projections : null;
  if (!projections || !Array.isArray(projections.services) || !Array.isArray(projections.staff) ||
    !isObject(projections.bookingConfig) || !isObject(projections.bookingConfig.features) ||
    !isObject(projections.bookingConfig.preferences)) return null;
  const validServices = projections.services.every((service) => isObject(service) &&
    typeof service.id === 'string' && typeof service.title === 'string' &&
    typeof service.price === 'number' && typeof service.durationMinutes === 'number' &&
    Array.isArray(service.employeeIds) && service.employeeIds.every((id) => typeof id === 'string') &&
    (service.showOnHomepage === undefined || typeof service.showOnHomepage === 'boolean') &&
    (service.priceVaries === undefined || typeof service.priceVaries === 'boolean'));
  const validStaff = projections.staff.every((employee) => isObject(employee) &&
    typeof employee.id === 'string' && typeof employee.firstName === 'string' &&
    typeof employee.lastName === 'string' && typeof employee.displayName === 'string' &&
    typeof employee.isOrchestrated === 'boolean' && typeof employee.isOwner === 'boolean' &&
    typeof employee.canAcceptCard === 'boolean');
  const features = projections.bookingConfig.features;
  const preferences = projections.bookingConfig.preferences;
  const uiFlags = isObject(preferences.uiFlags) ? preferences.uiFlags : null;
  const terminology = isObject(preferences.terminology) ? preferences.terminology : null;
  const validBookingConfig =
    ['enableCustomerSMS', 'enableEmployeeSMS', 'enablePayments', 'enableCart']
      .every((key) => typeof features[key] === 'boolean') &&
    ['requirePaymentForBooking', 'allowTips', 'allowAnyAvailableEmployee', 'orchestratorMode']
      .every((key) => typeof preferences[key] === 'boolean') &&
    (preferences.paymentDeposit === undefined || isValidPaymentDeposit(preferences.paymentDeposit)) &&
    typeof preferences.minimumBookingNoticeHours === 'number' &&
    ['staff', 'resource'].includes(String(preferences.bookingModel)) &&
    !!uiFlags &&
    ['showEmployeeBio', 'showEmployeeSocials', 'showEmployeeProfilePhoto', 'showEmployeePickerInBooking']
      .every((key) => typeof uiFlags[key] === 'boolean') &&
    !!terminology &&
    ['resourceSingular', 'resourcePlural', 'anyAvailableLabel', 'selectResourceLabel']
      .every((key) => typeof terminology[key] === 'string');
  if (!validServices || !validStaff || !validBookingConfig) return null;
  return {
    content: content as unknown as PublicSiteConfig,
    projections: projections as unknown as PublicOperationalProjections,
  };
};

const applyPublishedConfig = (
  published: PublicSiteConfig,
  projections: PublicOperationalProjections,
): HomepageRuntimeConfig => ({
  ...createEmptyBusinessConfig(),
  businessInfo: {
    name: published.business.displayName,
    timezone: published.business.timezone,
    address: published.business.address ?? '',
    phone: published.business.phone ?? '',
    email: published.business.email,
    logoImagePath: published.assets.logo,
    businessHours: published.business.businessHours,
  },
  socials: { ...published.socials },
  homepage: {
    ...createEmptyBusinessConfig().homepage,
    heroTitle: published.homepage.heroTitle,
    heroSubtitle: published.homepage.heroSubtitle,
    aboutUs: published.homepage.about,
    googleMapsSrc: published.homepage.mapUrl ?? '',
    logoPlacement: resolvePublishedLogoPlacement(published.homepage),
    heroImagePath: published.assets.hero ?? '',
    heroLogoImagePath: published.assets.heroLogo,
    gallery: published.homepage.gallery.map((item) => ({
      imagePath: item.image,
      title: item.title,
      description: item.description,
    })),
    reviews: published.homepage.reviews.map((review) => ({ ...review })),
  },
  preferences: {
    ...projections.bookingConfig.preferences,
    uiFlags: {
      ...projections.bookingConfig.preferences.uiFlags,
      showHomepageTeamSection: published.sections.showTeam,
      showHomepageReviewsSection: published.sections.showReviews,
      showHomepageGallerySection: published.sections.showGallery,
      showHomepageAboutSection: published.sections.showAbout,
      showEmployeePickerInBooking: published.booking.showEmployeePicker,
    },
  },
  features: {...projections.bookingConfig.features},
  paymentInfo: {owner: ''},
  payoutInfo: {useBusinessStripeAccount: false},
  publicSiteRuntime: {
    source: 'published',
    bookingEnabled: published.booking.enabled,
    bookingCtaLabel: published.booking.ctaLabel,
    showPrices: published.booking.showPrices,
    showEmployeePicker: published.booking.showEmployeePicker,
    theme: { ...published.theme },
  },
});

export const getHomepageRuntimeConfig = async (tenantIdInput: string): Promise<HomepageRuntimeConfig> => {
  const tenantId = validateTenantId(tenantIdInput);
  const publicSiteSnapshot = await getDoc(doc(db, 'public-sites', tenantId));
  const publishedConfig = publicSiteSnapshot.exists()
    ? parsePublicSiteDocument(tenantId, publicSiteSnapshot.data())
    : null;
  if (!publishedConfig) throw new Error('This tenant does not have a valid published site.');
  return applyPublishedConfig(publishedConfig.content, publishedConfig.projections);
};

const PUBLIC_HOSTNAME_PATTERN = /^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;

export const getTenantPublicOrigin = async (tenantIdInput: string): Promise<string> => {
  const tenantId = validateTenantId(tenantIdInput);
  const snapshot = await getDoc(doc(db, 'public-sites', tenantId));
  const value = snapshot.data();
  const hostname = typeof value?.primaryHostname === 'string'
    ? value.primaryHostname.trim().toLowerCase()
    : '';
  if (
    !snapshot.exists() ||
    value?.tenantId !== tenantId ||
    value?.availabilityState !== 'active' ||
    !PUBLIC_HOSTNAME_PATTERN.test(hostname)
  ) {
    throw new Error('This tenant does not have an active public hostname.');
  }
  if (hostname.endsWith('.localhost')) {
    const runtimePort = process.env.REACT_APP_TENANT_RUNTIME_PORT || '3001';
    return `http://${hostname}:${runtimePort}`;
  }
  return `https://${hostname}`;
};

export const createEmployeeBookingUrl = (
  publicOrigin: string,
  tenantIdInput: string,
  employeeId: string,
  _localBookingOrigin?: string,
): string => {
  validateTenantId(tenantIdInput);
  const target = new URL('/book-now', publicOrigin);
  target.searchParams.set('employee', employeeId);
  return target.toString();
};

const getPublicSite = async (tenantIdInput: string) => {
  const tenantId = validateTenantId(tenantIdInput);
  const snapshot = await getDoc(doc(db, 'public-sites', tenantId));
  const parsed = snapshot.exists() ? parsePublicSiteDocument(tenantId, snapshot.data()) : null;
  if (!parsed) throw new Error('This tenant does not have a valid published site.');
  return parsed;
};

export const fetchPublicEmployees = async (tenantId: string): Promise<Employee[]> => {
  const {projections} = await getPublicSite(tenantId);
  if (isPreviewBooking()) return [previewEmployee(projections.bookingConfig.preferences.terminology?.resourceSingular)];
  return projections.staff.map((employee) => ({
    id: employee.id,
    firstName: employee.firstName,
    lastName: employee.lastName,
    admin: false,
    email: '',
    phone: '',
    appointments: {},
    profilePictureImagePath: employee.image,
    bio: employee.bio,
    socials: employee.socials,
    active: true,
    bookable: true,
    isOrchestrated: employee.isOrchestrated,
    isOwner: employee.isOwner,
    canAcceptCard: employee.canAcceptCard,
  }));
};

export const fetchPublicServices = async (tenantId: string): Promise<Service[]> => {
  const {projections} = await getPublicSite(tenantId);
  const services: Service[] = projections.services.map((service) => ({
    id: service.id,
    title: service.title,
    description: service.description,
    price: service.price,
    time: service.durationMinutes,
    employees: [...service.employeeIds],
    imagePath: service.image,
    showOnHomepage: service.showOnHomepage ?? Boolean(service.image),
    priceVaries: service.priceVaries === true,
    questions: publicServiceQuestions(service.questions),
  }));
  return isPreviewBooking() ? withPreviewEmployee(services) : services;
};

// Questions are optional and read leniently: a malformed entry is skipped rather
// than hiding the business's whole site.
const publicServiceQuestions = (value: unknown): ServiceQuestion[] =>
  (Array.isArray(value) ? value : []).flatMap((entry) => {
    if (!isObject(entry) || typeof entry.id !== 'string' || typeof entry.label !== 'string' || !entry.label.trim()) {
      return [];
    }
    return [{ id: entry.id, label: entry.label.trim().slice(0, SERVICE_QUESTION_LABEL_LIMIT), required: entry.required === true }];
  }).slice(0, MAX_SERVICE_QUESTIONS);
